import { PatientDetailUtils } from '../../../../utils/PatientDetail.utils';
import { PatientScanOptionsMenu } from './PatientScanOptionsMenu.graphql';
import { PatientScanPillContent } from './PatientScanPillContent.graphql';
import { BrowserAnalyticsClientFactory } from '@orthly/analytics/dist/browser';
import { PracticeScreen, UnwrappedProductImageWrapper } from '@orthly/dentin';
import type { FragmentType } from '@orthly/graphql-inline-react';
import { getFragmentData, graphql } from '@orthly/graphql-inline-react';
import { parseProductUnitType } from '@orthly/items';
import { OrderFormatUtils } from '@orthly/shared-types';
import { Button, FlossPalette, styled, Text } from '@orthly/ui-primitives';
import React from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

export const SCAN_CARD_WIDTH = 265;

const ScanCard = styled('div', {
    shouldForwardProp: p => p !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
    padding: '12px',
    borderRadius: '12px',
    cursor: 'pointer',
    minWidth: `${SCAN_CARD_WIDTH}px`,
    background: selected ? FlossPalette.PRIMARY_BACKGROUND : FlossPalette.WHITE,
    border: selected ? `1px solid ${FlossPalette.PRIMARY_FOREGROUND}` : `1px solid ${FlossPalette.STROKE_LIGHT}`,
    '&:hover': {
        border: selected ? `1px solid ${FlossPalette.PRIMARY_FOREGROUND}` : `1px solid ${FlossPalette.STROKE_DARK}`,
    },
}));

const ScanCardInnerContainer = styled('div')({
    display: 'grid',
    gridTemplateRows: '1fr auto',
    gap: '12px',
});

const ScanMetaContainer = styled('div')({
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const ScanUpdatedContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
});

const ScanTitleContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
});

const ScanTitleInnerContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

const ScanActionButton = styled(Button)({
    padding: '0 8px',
    height: 'unset',
});

const TextWithClipping = styled(Text)({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '212px',
});

const PatientScanCardScan_Fragment = graphql(`
    fragment PatientScanCardScan_Fragment on PatientScan {
        ...PatientScanPillContentScan_Fragment

        updated_at
        product_unit_type
        scan_type
        doctor_name
        order_id
        scan_export_id
    }
`);

const PatientScanCardScanExport_Fragment = graphql(`
    fragment PatientScanCardScanExport_Fragment on scan_export {
        file_url
        deleted_at
        assets {
            number
        }
    }
`);

const PatientScanCardPatient_Fragment = graphql(`
    fragment PatientScanCardPatient_Fragment on PatientDTO {
        ...PatientScanOptionsMenuPatient_Fragment
        id
    }
`);

interface PatientScanCardProps {
    idx: number;
    patientScanCardFragment: FragmentType<typeof PatientScanCardPatient_Fragment>;
    scanCardFragment: FragmentType<typeof PatientScanCardScan_Fragment>;
    selectedScanCardFragment: FragmentType<typeof PatientScanCardScan_Fragment> | undefined | null;
    selectedScanExportFragment: FragmentType<typeof PatientScanCardScanExport_Fragment> | undefined | null;
    selectedScanExportLoading: boolean;
    selectedScanIndex: number | null;
    setSelectedScanIndex: (index: number | null) => void;
    setSelectedScanExportId: (id: string | null) => void;
    setShareWithPatientModalOpen: (open: boolean) => void;
}

export const PatientScanCard: React.VFC<PatientScanCardProps> = props => {
    const {
        selectedScanCardFragment,
        selectedScanExportFragment,
        patientScanCardFragment,
        scanCardFragment,
        selectedScanExportLoading,
        idx,
        selectedScanIndex,
        setSelectedScanIndex,
        setSelectedScanExportId,
        setShareWithPatientModalOpen,
    } = props;

    const navigate = useNavigate();

    const selected = selectedScanIndex === idx;

    const patientFragment = getFragmentData(PatientScanCardPatient_Fragment, patientScanCardFragment);
    const scanFragment = getFragmentData(PatientScanCardScan_Fragment, scanCardFragment);
    const selectedScanExport = getFragmentData(PatientScanCardScanExport_Fragment, selectedScanExportFragment);

    React.useEffect(() => {
        if (selectedScanExport && selectedScanExport.assets?.length === 0) {
            BrowserAnalyticsClientFactory.Instance?.track('Practice - Patient Page - Corrupted Scan Selected', {
                patientId: patientFragment.id,
                scanId: scanFragment.scan_export_id,
            });
        }
    }, [selectedScanExport, patientFragment.id, scanFragment.scan_export_id]);

    const handleClick = () => {
        BrowserAnalyticsClientFactory.Instance?.track('Practice - Patient Page - Scan Selected', {
            patientId: patientFragment.id,
            scanId: scanFragment.scan_export_id,
        });
        setSelectedScanIndex(idx);
        setSelectedScanExportId(scanFragment.scan_export_id);
    };

    const handleViewOrderClick = () => {
        BrowserAnalyticsClientFactory.Instance?.track('Practice - Patient Page - Order Opened', {
            $groups: { order: scanFragment.order_id ?? '' },
            patientId: patientFragment.id,
            scanId: scanFragment.scan_export_id,
        });
        navigate(`/${PracticeScreen.orders}/${scanFragment.order_id}`);
    };

    return (
        <ScanCard selected={selected} onClick={handleClick}>
            <ScanCardInnerContainer>
                <ScanMetaContainer>
                    <PatientScanPillContent scanFragment={scanFragment} />

                    <ScanUpdatedContainer>
                        <Text variant={'caption'} medium color={'DARK_GRAY'} align={'right'}>
                            {PatientDetailUtils.getPatientScanUpdatedAt(scanFragment)}
                        </Text>

                        <PatientScanOptionsMenu
                            selectedScanExport={selectedScanExport}
                            selectedScanExportLoading={selectedScanExportLoading}
                            selectedScanCardFragment={selectedScanCardFragment}
                            patientFragment={patientFragment}
                            selected={selected}
                            setShareWithPatientModalOpen={setShareWithPatientModalOpen}
                        />
                    </ScanUpdatedContainer>
                </ScanMetaContainer>

                <ScanTitleContainer>
                    <UnwrappedProductImageWrapper
                        product={parseProductUnitType(scanFragment.product_unit_type)}
                        size={24}
                        backgroundColor={'WHITE'}
                    />

                    <ScanTitleInnerContainer>
                        <TextWithClipping variant={'body2'} medium>
                            {scanFragment.scan_type}
                        </TextWithClipping>
                        <Text variant={'caption'} medium color={'DARK_GRAY'}>
                            {OrderFormatUtils.getDisplayedStaffMemberName(scanFragment.doctor_name)}
                        </Text>
                    </ScanTitleInnerContainer>
                </ScanTitleContainer>

                {scanFragment.order_id && (
                    <div>
                        <ScanActionButton variant={'ghost'} endIcon={'CallMadeIcon'} onClick={handleViewOrderClick}>
                            View order
                        </ScanActionButton>
                    </div>
                )}
            </ScanCardInnerContainer>
        </ScanCard>
    );
};
